import React, { useEffect, useState } from "react";
import Box from "@amzn/meridian/esm/components/box";
import Column from "@amzn/meridian/esm/components/column";
import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme/theme";
import Toggle from "@amzn/meridian/esm/components/toggle";
import zoomSdk from "@zoom/appssdk";

import summaryProgress from "../../../../images/summary_progress.svg";
import star from "../../../../images/star.svg";
import { AIMS_STATUS, FEATURE_FLAGS } from "../../../../constants";
import { updateSummaryReviewers } from "../../../../utils/summaryReviewerUtils";
import LogManager from "../../../../utils/logManager";
import { THEME_COLOR } from "../../../../styles/colors";

/**
 * A reusable visual component that indicates AiMS summarization is currently in progress.
 * This component is purely presentational and does not contain any business logic.
 * Shows disclaimer message to organizers who didn't start the summarization.
 */
const SummarizingIndicator = ({ rtmsRequesterName, isRtmsStartedByOther, alias, rtmsStreamId, meetingUuid, meetingId, aimsStatus, hasGermanyAttendee }) => {
  const [sendToAllAttendees, setSendToAllAttendees] = useState(true); // Default to true (on by default)
  const [showDisclaimer, setShowDisclaimer] = useState(true);

  useEffect(() => {
    if (isRtmsStartedByOther) {
      const timer = setTimeout(() => {
        setShowDisclaimer(false);
      }, 2 * 60 * 1000); // 2 minutes

      return () => clearTimeout(timer);
    }
  }, [isRtmsStartedByOther]);

  const handleToggleChange = async (checked) => {
    // Update local state immediately for better UX
    setSendToAllAttendees(checked);
    
    // Call API and revert on error
    const success = await updateSummaryReviewers({
      rtmsStreamId,
      meetingUuid,
      meetingId,
      alias,
      checked
    });
    
    if (!success) {
      // Revert the toggle state on error
      setSendToAllAttendees(!checked);
      return;
    }

    // Send Zoom chat message only if Germany attendee is present
    if (hasGermanyAttendee) {
      try {
        const message = checked 
          ? "You'll receive a meeting summary when it's ready. Sharing enabled."
          : "Only organizer will receive summary. Sharing disabled. Contact them for a copy or take notes.";
        
        await zoomSdk.sendMessageToChat({ message });
        LogManager.info(`Sent summary sharing ${checked ? 'enabled' : 'disabled'} message to chat`);
      } catch (error) {
        LogManager.error("Error sending summary sharing message to chat:", error);
      }
    }
  };

  return (
    <Column alignment="start" width="100%" spacing="none" spacingInset="none">
      {!isRtmsStartedByOther && rtmsStreamId && aimsStatus !== AIMS_STATUS.SUMMARY_TO_ORGANIZER && (
        <>
          {FEATURE_FLAGS.ALLOW_SUMMARY_DISTRIBUTION_TOGGLE && (
            <Row spacing="none" spacingInset="none">
              <Column width="72px"/>
              <Column spacing="300">
                <Column alignmentHorizontal="start" width="220px">
                  <Toggle
                    checked={sendToAllAttendees}
                    onChange={handleToggleChange}
                  >
                    <Text type="b200">
                      Send meeting summary to all attendees
                    </Text>
                  </Toggle>
                </Column>
                <Text color="secondary" type="b100">Non-Amazon attendees don't receive summaries.</Text>
              </Column>
            </Row>
          )}
        </>
      )}
      <Column height="12px" spacing="none" />
      <Row spacing="none" spacingInset="none">
        <Column width="15px" />
        <img src={summaryProgress} alt="" />
      </Row>
      <Column height="36px" spacing="none" />
      <Column spacing="none" spacingInset="none">
        <Row spacing="none" spacingInset="none">
          <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
            <Box type="outline" spacingInset="none" minWidth="68px"></Box>
          </Theme>
          <Text type="h50" alignment="center">
            Summarizing...
          </Text>
        </Row>
        {isRtmsStartedByOther && showDisclaimer && (
          <>
            <Column height="16px" spacing="none" />
            <Row spacing="none" spacingInset="none">
              <Column width="68px"/>
              <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
                <Box type="outline" spacingInset="none" width="253px">
                  <Theme tokens={{ textListSpacingVertical: "30px" }}>
                    <Text type="b200" alignment="start" color="secondary">
                      If you don't agree to meeting summary, remain on mute, leave the meeting, or ask the organizer to stop summarizing meetings at any time.
                    </Text>
                  </Theme>
                </Box>
              </Theme>
            </Row>
          </>
        )}
        {rtmsRequesterName && isRtmsStartedByOther && (
          <>
            <Column height="48px" spacing="none" />
            <Row spacing="none" spacingInset="none">
              <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
                <Box type="outline" spacingInset="none" minWidth="68px"></Box>
              </Theme>
              <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.LIGHT_TEAL }}>
                <Box type="outline" spacingInset="400" width="252px">
                  <Row spacing="none">
                    <img src={star} alt="star" />
                    <Theme tokens={{ boxBorderColorOutline: THEME_COLOR.WHITE }}>
                      <Box type="outline" spacingInset="none" minWidth="8px"></Box>
                    </Theme>
                    <Column spacing="none">
                      <Text type="h50" alignment="start">
                        {rtmsRequesterName}
                      </Text>
                      <Text type="b200" alignment="start">
                        turned on meeting summary
                      </Text>
                    </Column>
                  </Row>
                </Box>
              </Theme>
            </Row>
          </>
        )}
      </Column>
    </Column>
  );
};

export default SummarizingIndicator;
